import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Layout from '../../components/Layout/Layout';
import DataTable, { useServerTable } from '../../components/ui/DataTable';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/Modal';
import PackageWizard from './Wizard/Wizard';
import { PackagesProvider, usePackages } from '../../contexts/PackagesContext';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';
import { useDocumentTitle } from '../../config/utils';

export default function PackagesListPage() {
  return (
    <PackagesProvider>
      <PackagesList />
    </PackagesProvider>
  );
}

function PackagesList() {
  useDocumentTitle('Packages');
  const { showToast }        = useGlobal();
  const { selectedPackage, setSelectedPackage, resetWizard } = usePackages();

  const [wizardOpen, setWizardOpen]     = useState(false);
  const [orderOpen, setOrderOpen]       = useState(false);
  const [deleteItem, setDeleteItem]     = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [orderedList, setOrderedList]   = useState([]);

  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/packages/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { order: 'asc' }, filters: {},
    });
    return res.data;
  }, []);

  const table = useServerTable(fetcher);

  const openCreate = () => {
    setSelectedPackage({});
    resetWizard();
    setWizardOpen(true);
  };

  const openEdit = (pkg) => {
    setSelectedPackage(pkg);
    setWizardOpen(true);
  };

  const openOrder = () => {
    const sorted = [...table.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setOrderedList(sorted);
    setOrderOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.delete(`/datatable/packages/${deleteItem.id}/delete`);
      showToast('success', 'Deleted', `Package "${deleteItem.title}" deleted.`);
      setDeleteItem(null);
      table.refresh();
    } catch {
      showToast('error', 'Error', 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const saveOrder = async () => {
    try {
      const order = orderedList.map((p, i) => ({ id: p.id, order: i + 1 }));
      await adminApi.post('/packages/order', { packages: order });
      showToast('success', 'Order Saved', 'Package order updated.');
      setOrderOpen(false);
      table.refresh();
    } catch {
      showToast('error', 'Error', 'Could not save order.');
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(orderedList);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setOrderedList(items);
  };

  const columns = [
    { key: 'title',            header: 'Title' },
    { key: 'show_in_dashboard', header: 'In Dashboard', render: r => (
      <Badge variant={r.show_in_dashboard ? 'teal' : 'muted'}>
        {r.show_in_dashboard ? 'Visible' : 'Hidden'}
      </Badge>
    )},
    { key: 'order', header: 'Order', render: r => (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-xs font-bold">
        {r.order}
      </span>
    )},
  ];

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Packages</h1>
          <p className="text-text-muted text-sm mt-0.5">{table.total} packages</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openOrder} className="btn-secondary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
            </svg>
            Reorder
          </button>
          <button onClick={openCreate} className="btn-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Package
          </button>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          {...table}
          onPageChange={table.setPage}
          onPerPageChange={table.setPerPage}
          actions={row => (
            <div className="flex items-center gap-1">
              <button onClick={() => openEdit(row)} className="btn-icon" title="Edit">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M16.732 3.732a2.5 2.5 0 013.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button onClick={() => setDeleteItem(row)} className="btn-icon text-danger hover:bg-danger/10" title="Delete">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          )}
        />
      </div>

      {/* Wizard Modal */}
      <Modal
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); resetWizard(); }}
        title={Object.keys(selectedPackage).length > 0 ? 'Update Package' : 'Create Package'}
        size="lg"
      >
        <PackageWizard onDone={() => { setWizardOpen(false); resetWizard(); table.refresh(); }} />
      </Modal>

      {/* Reorder Modal */}
      <Modal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        title="Reorder Packages"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOrderOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={saveOrder}>Save Order</button>
          </>
        }
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="packages-order">
            {provided => (
              <ul {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5">
                {orderedList.map((pkg, index) => (
                  <Draggable key={pkg.id} draggableId={String(pkg.id)} index={index}>
                    {(provided, snapshot) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          snapshot.isDragging
                            ? 'border-accent bg-accent/10'
                            : 'border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04]'
                        }`}
                      >
                        <svg className="w-4 h-4 text-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                        <span className="text-sm text-text-main flex-1">{pkg.title}</span>
                        <span className="text-xs text-text-muted">#{index + 1}</span>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </Modal>

      <ConfirmModal
        open={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        message={`Delete package "${deleteItem?.title}"? This cannot be undone.`}
      />
    </Layout>
  );
}
